import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";
import { createExpense } from "@/api/expenses/create-expenses";
import { getCompany } from "@/api/company/get-Company";
import React from "react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CalendarIcon, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { Pagination } from "@/components/pagination";
import { queryClient } from "@/lib/reactQuery";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fetchExpenses } from "@/components/expenses/getExpense";
import { usePageState, useSetPage } from "@/components/utils/paginationStates";
import ExpenseTable from "@/components/expenses/ExpenseTable";
import {
  useSelectedMonth,
  useSelectedState,
  useSelectedYear,
  useTempSelectedMonth,
  useTempSelectedYear,
} from "@/components/expenses/expenseStates";
import { useDialog } from "@/components/utils/isDialogOpen";

const createExpenseSchema = z.object({
  expenseDescription: z.string({ message: "Enter expense description" }),
  expenseAmount: z.number({ message: "Enter amount" }).nonnegative(),
  paidTo: z.string({ message: "Enter vendor" }),
  paymentMethod: z.string({ message: "Enter payment method" }),
  expenseDate: z.coerce.date(),
});

type CreateExpenseType = z.infer<typeof createExpenseSchema>;

export function RegisterExpense() {
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateExpenseType>({
    resolver: zodResolver(createExpenseSchema),
  });

  console.log("Form values:", control._formValues);

  ///////////////////////// Create Expense ////////////////

  const { mutateAsync: CreateBuildingFn } = useMutation({
    mutationKey: ["expense"],
    mutationFn: createExpense,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] }); // Refetch the expenses to update the table
    },
  });

  const { isDialogOpen, setIsDialogOpen } = useDialog();

  async function handleCreateExpense(data: CreateExpenseType) {
    try {
      const companyRes = await getCompany(); // fetch the entire company object
      const companyId = companyRes.company[0].id;

      const { status } = await CreateBuildingFn({
        expenseDescription: data.expenseDescription,
        expenseAmount: data.expenseAmount,
        paidTo: data.paidTo,
        expenseDate: data.expenseDate,
        paymentMethod: data.paymentMethod,
        companyId: companyId,
      });
      if (status === 201) {
        toast.success("Expense created successfully");
        reset();
      }
    } catch (err) {
      console.log(err);
      toast.error("Error creating expense");
    }
  }

  ////////////////////////////////////////////////////////

  /////////// Get expense & Pagination ///////////////////

  const { tempSelectedMonth, setTempSelectedMonth } = useTempSelectedMonth();
  const { selectedMonth, setSelectedMonth } = useSelectedMonth();
  const { tempSelectYear, setTempSelectYear } = useTempSelectedYear();
  const { selectedYear, setSelectedYear } = useSelectedYear();

  const { setPageSize } = usePageState();
  const { setCurrentPage } = useSetPage();
  const { expenses, currentPage } = fetchExpenses();

  const totalPages = expenses?.totalPages || 1;
  const totalItems = expenses?.totalExpenses || 0;

  ////////////////////////////////////////////////////////

  //////////////// Delete expense ///////////////////////

  ////////////////////////////////////////////////////////

  /////////////////////// Edit Expense //////////////////

  const { setSelectedExpense } = useSelectedState();

  /////////////////////////////////////////////////////////

  ///////////////// Filtering /////////////////////////////

  function handleFilterSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSelectedMonth(tempSelectedMonth); // Set selectedMonth only when 'Apply Filter' is pressed
    setSelectedYear(tempSelectYear); // Set selectedYear only when 'Apply Filter' is pressed
  }

  const months = Array.from({ length: 12 }, (_, index) => ({
    value: (index + 1).toString(),
    label: new Intl.DateTimeFormat("en", { month: "long" }).format(
      new Date(0, index),
    ),
  }));

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1999 }, (_, index) => ({
    value: (currentYear - index).toString(),
    label: (currentYear - index).toString(),
  }));

  return (
    <div>
      <div className="flex h-14 items-center rounded-b-2xl bg-[#16A34A] bg-opacity-40 px-10">
        <h3 className="p-5 font-Hemmers text-3xl text-black">Expenses</h3>
      </div>
      <div className="py-2">
        <div className="flex items-center justify-between p-4">
          {/* Left-aligned New + button with its own form */}
          <Dialog
            open={isDialogOpen}
            onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) {
                setSelectedExpense(null); // Reset selectedExpense when dialog is closed
              }
            }}
          >
            <DialogTrigger asChild>
              <Button
                onClick={() => setSelectedExpense(null)}
                className="gap-x-2 border border-black bg-[#FAFAFA] text-lg text-black hover:bg-gray-200"
              >
                <Plus className="size-4" /> Create New
              </Button>
            </DialogTrigger>
            <DialogContent className="border-transparent bg-inherit">
              <DialogHeader>
                <DialogTitle className="text-black">
                  Create New Expense
                </DialogTitle>
                <DialogDescription>
                  Fill in to register a new expense
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit(handleCreateExpense)}>
                <div className="flex space-x-4">
                  <div className="flex w-[350px] flex-col">
                    <label
                      htmlFor="expenseDescription"
                      className="block text-sm font-medium text-black"
                    >
                      Expense Description
                    </label>
                    <Input
                      {...register("expenseDescription", {
                        required: "Expense description is required",
                      })}
                      type="text"
                      id="expenseDescription"
                      autoComplete="off"
                      className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                    />
                    {errors.expenseDescription && (
                      <span className="text-sm text-rose-500">
                        {errors.expenseDescription.message}
                      </span>
                    )}
                  </div>

                  <div className="flex w-24 flex-col">
                    <label
                      htmlFor="expenseAmount"
                      className="block text-sm font-medium text-black"
                    >
                      Price
                    </label>
                    <Input
                      {...register("expenseAmount", {
                        required: "Expense amount is required",
                        valueAsNumber: true,
                      })}
                      type="number"
                      id="expenseAmount"
                      autoComplete="off"
                      className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                    />
                    {errors.expenseAmount && (
                      <span className="text-sm text-rose-500">
                        {errors.expenseAmount.message}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex space-x-4">
                  <div className="mt-4 w-1/2">
                    <label
                      htmlFor="paidTo"
                      className="block text-sm font-medium text-black"
                    >
                      Vendor
                    </label>
                    <Input
                      {...register("paidTo", {
                        required: "Vendor is required",
                      })}
                      type="text"
                      id="paidTo"
                      autoComplete="off"
                      className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                    />
                    {errors.paidTo && (
                      <span className="text-sm text-rose-500">
                        {errors.paidTo.message}
                      </span>
                    )}
                  </div>

                  <div className="mt-4 w-1/2">
                    <label
                      htmlFor="paymentMethod"
                      className="block text-sm font-medium text-black"
                    >
                      Payment Method
                    </label>
                    <Controller
                      name="paymentMethod"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <Select
                          onValueChange={field.onChange}
                          value={field.value || ""}
                        >
                          <SelectTrigger className="mt-1 w-full border border-zinc-300 bg-white text-left text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Credit Card">
                              Credit Card
                            </SelectItem>
                            <SelectItem value="Cash">Cash</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.paymentMethod && (
                      <span className="text-sm text-rose-500">
                        {errors.paymentMethod.message}
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-4 w-full">
                  <label
                    htmlFor="expenseDate"
                    className="block text-sm font-medium text-black"
                  >
                    Expense Date
                  </label>
                  <Controller
                    name="expenseDate"
                    control={control}
                    defaultValue={undefined}
                    render={({ field }) => (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "bordeer w-full justify-start border border-input border-zinc-300 bg-white text-left font-normal text-zinc-900 shadow-sm hover:bg-gray-200 focus:border-zinc-500 focus:ring-zinc-500",
                              !field.value && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="pointer-events-auto w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  />
                  {errors.expenseDate && (
                    <span className="text-sm text-rose-500">
                      {errors.expenseDate.message}
                    </span>
                  )}
                </div>

                <div className="mt-4">
                  <Button
                    disabled={isSubmitting}
                    className="w-full bg-[#16A34A] hover:bg-[#1bc459]"
                    type="submit"
                    onClick={async () => setIsDialogOpen(false)}
                  >
                    Create Expense
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          <div className="ml-auto flex items-center gap-4">
            <Popover>
              <PopoverTrigger asChild>
                <Button className="border border-black bg-[#FAFAFA] text-lg text-black hover:bg-gray-200">
                  Add Filter
                </Button>
              </PopoverTrigger>
              <PopoverContent className="bg-[#FAFAFA] p-4">
                <form onSubmit={handleFilterSubmit}>
                  <div className="flex gap-x-2">
                    <div className="mt-4 w-1/2">
                      <label
                        htmlFor="filterMonth"
                        className="block text-sm font-medium text-black"
                      >
                        Select Month
                      </label>
                      <Select
                        onValueChange={(value) =>
                          setTempSelectedMonth(
                            value ? parseInt(value) : undefined,
                          )
                        }
                        value={
                          tempSelectedMonth !== undefined
                            ? tempSelectedMonth.toString()
                            : ""
                        }
                      >
                        <SelectTrigger className="mt-1 rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm">
                          <SelectValue placeholder="All Months" />
                        </SelectTrigger>
                        <SelectContent>
                          {months.map((month) => (
                            <SelectItem key={month.value} value={month.value}>
                              {month.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="mt-4 w-1/2">
                      <label
                        htmlFor="filterYear"
                        className="block text-sm font-medium text-black"
                      >
                        Select Year
                      </label>
                      <Select
                        onValueChange={(value) =>
                          setTempSelectYear(value ? parseInt(value) : undefined)
                        }
                        value={
                          tempSelectYear !== undefined
                            ? tempSelectYear.toString()
                            : ""
                        }
                      >
                        <SelectTrigger className="mt-1 rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm">
                          <SelectValue placeholder="Years" />
                        </SelectTrigger>
                        <SelectContent>
                          {years.map((year) => (
                            <SelectItem key={year.value} value={year.value}>
                              {year.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="flex gap-x-1">
                      <Button
                        className="w-full bg-[#16A34A] hover:bg-[#1bc459]"
                        type="submit"
                      >
                        Apply Filter
                      </Button>
                      {(selectedMonth || selectedYear) && (
                        <Button
                          className="border border-black bg-[#FAFAFA] text-black hover:bg-gray-200"
                          onClick={() => {
                            setSelectedMonth(undefined);
                            setSelectedYear(undefined);
                            setTempSelectedMonth(undefined);
                            setTempSelectYear(undefined);
                          }}
                        >
                          Clear Filter
                        </Button>
                      )}
                    </div>
                  </div>
                </form>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
      <div className="mx-4 rounded-lg border border-black bg-[#FAFAFA] p-4">
        <ExpenseTable />
        <Pagination
          items={totalItems}
          page={currentPage}
          pages={totalPages}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        ></Pagination>
      </div>
    </div>
  );
}
